import React, {
	useMemo,
	useEffect,
	useState,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
	CUSTOM_DESTINATION_OPTIONS as DESTINATION_OPTIONS,
	DESTINATION_TYPE,
	Destination,
} from '../../../stores/types/broadcasting';
import { GreenroomState as GS } from '../../../stores/types/types';
import * as BroadcastActions from '../../../stores/actions/broadcasting';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import Switch from '../../general-components/switch';
import { StreamSettingLocation } from './streaming-settings';
import LoadingSpinner from '../../general-components/loading-spinner';
import useValidateRtmp from '../../../hooks/use-validate-rtmp';
import styled from 'styled-components';

interface Props {
	meetingId: number;
	location: string;
	goLiveButtonClicked: boolean;
	destination: Destination;
	maxSelected: boolean;
	isEditing: boolean;
	isNew?: boolean;
}

const CARD_HEIGHTS = {
	HIDE: 0,
	CLOSE: 72,
	OPEN: 230,
};

const blGrayscale200 = '#E5E4EC';
const blGrayscale400 = '#908CA0';
const rem100px = '7.142rem';
const rem12px = '0.8571rem';
const rem16px = '1.143rem';
const semi = 500;
const transitionDurationFast = '250ms';
const transitionTimingDefault = 'ease-in-out';
const transitionFast = `${transitionDurationFast} ${transitionTimingDefault}`;
export const DestinationDiv = styled.div`
	width: 275px;
	display: flex;
	align-items: center;
	margin: 0;
	padding: 8px 16px;
	border-radius: ${rem100px};
	border: 1px solid ${blGrayscale400};
	background: transparent;
	color: ${blGrayscale200};
	font-size: ${rem12px};
	line-height: ${rem16px};
	font-weight: ${semi};
	transition: border-color ${transitionFast};

	&::placeholder {
		color: ${blGrayscale400};
	}

	&:hover, &:focus {
		border: 1px solid ${blGrayscale200};
	}

	input {
		border: none;
		margin: 0;
		padding: 0;
		background: transparent;
		color: #E5E4EC;
		font-size: 14px;
	}
`;

export const DestinationInput = styled.input`
	appearance: none;
    outline: none;
	color: white;
	::hover, ::focus, ::active, &input[type="text"] {
		border: none;
	}
`;

export const CopyButton = styled.button`
	margin-left:5px;
`;

export const ShowButton = styled.button`
	margin-right:5px;
`;

const DestinationSpan = styled.span`
	width: 20%;
`;

const streamKeyHidden = '●●● ●●● ●●●';
export default function DestinationCard(props: Props) {
	const dispatch = useDispatch();
	const token = useSelector((s: GS) => s.SessionReducer.token);

	const {
		meetingId,
		location,
		goLiveButtonClicked,
		destination,
		maxSelected,
		isEditing,
		isNew = false,
	} = props;

	const [editDest, setEditDest] = useState<Destination>(destination);
	const [elHeight, setElHeight] = useState<number>(isNew ? CARD_HEIGHTS.HIDE : CARD_HEIGHTS.CLOSE);
	const [savingDest, setSavingDest] = useState<Destination | null>(null);
	const [revealStreamKey, setRevealStreamKey] = useState<boolean>(false);
	const cardRef = useRef<HTMLDivElement>(null);

	const {
		isValidatingRtmpEndpoint,
		rtmpOverrideModal,
		validateRtmpEndpoint,
	} = useValidateRtmp();

	const {
		id,
		logo,
		type,
		selected,
	} = destination;

	const {
		name,
		url,
		streamKey,
	} = editDest;

	const isOpen = useMemo(() => isEditing && !savingDest, [isEditing, savingDest]);
	const isDefaultDestination = useMemo(() => !id && !isNew, [id, isNew]);

	const cardStyle = useMemo(() => {
		const marginBottom = location === StreamSettingLocation.MetaMenu ? '8px' : '16px';
		return {
			height: `${elHeight}px`,
			padding: elHeight ? '16px 24px' : '0px 24px',
			marginBottom: elHeight ? marginBottom : '0px',
		};
	}, [elHeight, location]);

	const logoImage = useMemo(() => {
		const destType = DESTINATION_OPTIONS.find((o) => o.value === type) || DESTINATION_OPTIONS[0];
		if (type !== DESTINATION_TYPE.OTHER) return destType.description;
		return logo || destType.description;
	}, [logo, type]);

	function handleEditDestination(destId: string) {
		isNew
			? dispatch(BroadcastActions.setAddingDestination(null))
			: dispatch(BroadcastActions.setEditingDestination(destId));
	}

	function resetFields(closeCard: boolean = true) {
		setEditDest({
			...destination,
			name: isDefaultDestination ? DESTINATION_OPTIONS[0].label : destination.name ?? '',
		});
		if (closeCard) handleEditDestination('');
	}

	function cancelClick() {
		isNew ? setElHeight(CARD_HEIGHTS.HIDE) : resetFields();
	}

	function handleClick(e: any) {
		const path = e.composedPath();
		const len = path.length;
		let keepOpen = false;

		for (let i = 0; i < len; i += 1) {
			if (path[i].className?.includes) {
				if ((typeof path[i].className === 'string'
					&& (path[i].className.includes('destination-card edit')
						|| path[i].className.includes('destination-card-edit')
						|| path[i].className.includes('destination-options'))
				)) {
					keepOpen = true;
					break;
				}
			}
		}

		if (!keepOpen) {
			cancelClick();
		}
	}

	async function handleSaveDestination() {
		if (!token) return;
		setSavingDest({ ...editDest, url: url.replace(/\s+/g, ''), streamKey: streamKey.trim() });
	}

	async function handleToggleDestination() {
		if (!token) return;
		dispatch(BroadcastActions.updateDestination(
			token,
			meetingId,
			{ ...destination, selected: !selected },
		));
	}

	function getScrollContainer(containerLocation: string) {
		switch (containerLocation) {
			case StreamSettingLocation.MetaMenu:
				return document.getElementById('meta-event-settings');
			case StreamSettingLocation.StreamModal:
				return cardRef.current?.parentElement ?? null;
			default:
				return null;
		}
	}

	useEffect(() => {
		resetFields(false);
		if (savingDest) setSavingDest(null);
	}, [destination]);

	useEffect(() => {
		if (isEditing || isNew) window.addEventListener('click', handleClick);
		return () => window.removeEventListener('click', handleClick);
	}, [isEditing, isNew]);

	useEffect(() => {
		isOpen ? setElHeight(CARD_HEIGHTS.OPEN) : setElHeight(CARD_HEIGHTS.CLOSE);
	}, [isOpen]);

	const copyStreamKey = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		navigator.clipboard.writeText(streamKey);
	}

	const copyRTMPAddress = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		navigator.clipboard.writeText(url);
	}

	const enableRevealStreamKey = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		
		setRevealStreamKey(true);
		setTimeout(() => {
			setRevealStreamKey(false);
		}, 5000);
	}

	return (
		<div
			data-testid="destination-card"
			className={classNames('destination-card', { edit: isOpen, disabled: goLiveButtonClicked })}
			style={cardStyle}
			onTransitionEnd={async ({ propertyName }) => {
				if (propertyName !== 'height' || !token) return;

				// canceling a new destination
				if (isNew && elHeight === CARD_HEIGHTS.HIDE) {
					handleEditDestination('');
					return;
				}

				// remove destination animation has completed and it can be removed from the db
				if (
					(isOpen || !isEditing)
					&& elHeight === CARD_HEIGHTS.HIDE && id
				) {
					dispatch(BroadcastActions.deleteDestination(token, meetingId, id));
				}

				// save destination animation has completed and it can be added to the db
				if (!isOpen && !!savingDest) {
					id || isDefaultDestination
						? dispatch(BroadcastActions.updateDestination(token, meetingId, savingDest))
						: dispatch(BroadcastActions.addDestination(token, meetingId, savingDest));
				}

				// scroll to card if it is open
				if (isOpen && elHeight === CARD_HEIGHTS.OPEN) {
					const scrollContainer = getScrollContainer(location);
					if (cardRef.current && scrollContainer) {
						const offset = location === StreamSettingLocation.MetaMenu ? 72 : 0;

						const containerTop = scrollContainer.scrollTop;
						const containerBottom = containerTop + scrollContainer.clientHeight;

						const cardMarginBottom = parseFloat(cardRef.current.style.marginBottom);
						// 16px above the card
						const cardTop = cardRef.current.offsetTop - cardMarginBottom + offset;
						// 16px below the card
						const cardBottom = cardTop + cardRef.current.clientHeight + (cardMarginBottom * 2);

						// if card is outside the visible area, scroll to it
						const above = cardTop < containerTop;
						const below = cardBottom > containerBottom;
						if (above || below) {
							scrollContainer.scrollTo({
								top: above ? cardTop : cardBottom - scrollContainer.clientHeight,
								behavior: 'smooth',
							});
						}
					}
				}
			}}
			ref={cardRef}
		>
			{!isOpen && (
				<div className="destination-header">
					<div className="destination-label">
						<img src={logoImage} alt={logoImage} />
						<span className="bl-font-s1">{isDefaultDestination ? 'Brandlive' : name}</span>
					</div>
					<div className="destination-controls">
						{isOpen ? (
							<button className="x-button" onClick={() => handleEditDestination('')}>
								<Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={13.33} />
							</button>
						) : (
							<>
								<button
									onClick={() => { if (!savingDest) handleEditDestination(String(id ?? 0)); }}
									className='margin-right-small'
								>
									<Icon name={ICONS.EDIT} color={COLORS.TEXT_GRAY_400} size={13.33} />
								</button>
								<Switch
									on={selected}
									onChange={() => { if (!savingDest) handleToggleDestination(); }}
									className={`green ${maxSelected && !selected ? 'disabled' : ''}`}
								/>
							</>
						)}
					</div>
				</div>
			)}
			{isOpen && (
				<div className="destination-card-edit">
					<div className="destination-card-edit-row">
						<img src={logoImage} alt={logoImage} />
						<input
							type="text"
							className="destination-input"
							onChange={({ target }) => setEditDest({ ...editDest, name: target.value })}
							value={isDefaultDestination ? 'Brandlive' : name}
							placeholder="Name your destination..."
							disabled={isDefaultDestination}
						/>
					</div>
					<div className="destination-card-edit-row">
						<DestinationSpan className="bl-font-p2 grayscale-300">RTMP Address:</DestinationSpan>
						<DestinationDiv >
							<DestinationInput
								type="text"
								onChange={({ target }) => setEditDest({ ...editDest, url: target.value })}
								value={url}
								placeholder="Paste RTMP address here..."
								disabled={isDefaultDestination}
							/>
							<CopyButton className="copy-button" onClick={(e) => copyRTMPAddress(e)}>
								<Icon name={ICONS.COPY_FILLED} color={COLORS.TEXT_GRAY_400} size={10} />
							</CopyButton>
						</DestinationDiv>
					</div>
					<div className="destination-card-edit-row">
						<DestinationSpan className="bl-font-p2 grayscale-300">Stream Key:</DestinationSpan>
						<DestinationDiv>
						<ShowButton className="copy-button" onClick={(e) => enableRevealStreamKey(e)}>
								<Icon name={ICONS.EYE_SHOWN} color={COLORS.TEXT_GRAY_400} size={10} />
							</ShowButton>
							<DestinationInput
								type={revealStreamKey ? "text" : "password"}
								onChange={({ target }) => {
									if (target.value !== streamKeyHidden) {
										setEditDest({ ...editDest, streamKey: target.value });
									}
								}}
								onClick={() => setRevealStreamKey(false)}
								value={streamKey}
								placeholder={"Paste stream key here..."}
								disabled={isDefaultDestination}
							/>
							<CopyButton className="copy-button" onClick={(e) => copyStreamKey(e)}>
								<Icon name={ICONS.COPY_FILLED} color={COLORS.TEXT_GRAY_400} size={10} />
							</CopyButton>
						</DestinationDiv>
					</div>
					<div className="destination-card-edit-row">
						<button
							className="flex-center transparent"
							onClick={() => { setElHeight(CARD_HEIGHTS.HIDE); }}
							disabled={isDefaultDestination}
						>
							<Icon name={ICONS.MEDIA_DELETE} size={16} color={COLORS.RED} />
							<h5 className="bl-font-l1 semantic-red-400 padding-left-small margin-none">Delete Destination</h5>
						</button>
						<div className="flex-center">
							<button
								className="padding-button-small background-lighter border-radius-100 margin-right-small"
								disabled={false}
								onClick={cancelClick}
							>
								<label className="bl-font-l2 grayscale-200 pointer-events-none">Cancel</label>
							</button>
							<button
								className="padding-button-small background-green border-radius-100 transition-opacity"
								disabled={isValidatingRtmpEndpoint}
								onClick={() => validateRtmpEndpoint(
									name ?? '',
									url,
									streamKey,
									3,
									handleSaveDestination,
								)}
							>
								{isValidatingRtmpEndpoint && (
									<>
										<LoadingSpinner
											height={10}
											width={10}
											color="#09090A"
											stroke={10}
											style={{ display: 'flex', margin: 'none' }}
										/>
										&nbsp;
									</>
								)}
								<label className="bl-font-l2 grayscale-1200 pointer-events-none">Save</label>
							</button>
						</div>
						{rtmpOverrideModal}
					</div>
				</div>
			)}
		</div>
	);
}
